import { cloneElement, useEffect, useId, useRef, useState } from "react";

/**
 * AppTooltip — professional accessible tooltip.
 *
 * Usage:
 *   <AppTooltip content="..." placement="top">
 *     <button>Hover me</button>
 *   </AppTooltip>
 *
 * Rules:
 * - White background, navy text, thin gray border, soft shadow, small arrow
 * - max-width 280px, 1–3 lines preferred
 * - Hover, focus, and tap support
 * - Escape closes
 * - Keyboard accessible
 * - 150ms open delay, 100ms close delay
 */
export default function AppTooltip({
  content,
  children,
  placement = "top",
  delayOpen = 150,
  delayClose = 100,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const wrapRef = useRef(null);
  const id = useId();

  const cancelTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const scheduleOpen = () => {
    cancelTimers();
    openTimer.current = setTimeout(() => setOpen(true), delayOpen);
  };
  const scheduleClose = () => {
    cancelTimers();
    closeTimer.current = setTimeout(() => setOpen(false), delayClose);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  // Tap-to-toggle on mobile
  const handleClick = (e) => {
    if (e.currentTarget.matches(":hover") && window.matchMedia?.("(hover: hover)").matches) return;
    cancelTimers();
    setOpen((v) => !v);
  };

  // Inject aria-describedby + handlers on the single child
  const trigger = cloneElement(children, {
    ref: (node) => {
      wrapRef.current = node;
      const oldRef = children.ref;
      if (typeof oldRef === "function") oldRef(node);
      else if (oldRef && "current" in oldRef) oldRef.current = node;
    },
    "aria-describedby": open ? id : undefined,
    onMouseEnter: (e) => {
      scheduleOpen();
      children.props.onMouseEnter?.(e);
    },
    onMouseLeave: (e) => {
      scheduleClose();
      children.props.onMouseLeave?.(e);
    },
    onFocus: (e) => {
      scheduleOpen();
      children.props.onFocus?.(e);
    },
    onBlur: (e) => {
      scheduleClose();
      children.props.onBlur?.(e);
    },
    onClick: (e) => {
      handleClick(e);
      children.props.onClick?.(e);
    },
  });

  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  };
  const arrowClasses = {
    top: "left-1/2 -translate-x-1/2 top-full -mt-px border-t-[6px] border-x-[6px] border-x-transparent border-t-white",
    bottom:
      "left-1/2 -translate-x-1/2 bottom-full -mb-px border-b-[6px] border-x-[6px] border-x-transparent border-b-white",
    left: "top-1/2 -translate-y-1/2 left-full border-l-[6px] border-y-[6px] border-y-transparent border-l-white",
    right:
      "top-1/2 -translate-y-1/2 right-full border-r-[6px] border-y-[6px] border-y-transparent border-r-white",
  };

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={cancelTimers /* keep open while pointer on bubble area */}
    >
      {trigger}
      {open ? (
        <span
          role="tooltip"
          id={id}
          data-testid={testId}
          className={[
            "absolute z-50 max-w-[280px] w-max",
            "bg-white text-[#0F1D2D] text-[12.5px] leading-relaxed",
            "border border-[#E5E7EB] shadow-[0_12px_32px_-12px_rgba(15,29,45,0.22)]",
            "px-3.5 py-2.5",
            "animate-[fadeIn_120ms_ease-out]",
            positionClasses[placement] || positionClasses.top,
          ].join(" ")}
        >
          {content}
          <span
            aria-hidden
            className={["absolute h-0 w-0", arrowClasses[placement] || arrowClasses.top].join(" ")}
          />
        </span>
      ) : null}
    </span>
  );
}
